import {
  Modal,
  ModalContent,
  ModalActions,
  TextInput,
  Button,
  Switch,
  Icon,
  ModalHeader,
  Header,
  Text,
  FieldLabel,
  FieldValue,
} from '@lightspeed/design-system-react'
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { T, useT } from '@transifex/react'
import { useProductsStore } from '../../ng-compat/hooks'
import { QuickKeyColourPalette } from './QuickKeyColourPalette'
import { QuickKeyColour, QuickKeyNodeType } from './utils/qk.constants'
import {
  ApiNode,
  AttributeKey,
  QuickKeyFolder,
  QuickKeyKey,
} from './utils/qk-types'
import { QKKey } from './nodes/QKKey'

interface EditQuickKeyModalProps {
  quickKey: QuickKeyKey | QuickKeyFolder
  close: () => void
  updateNode: (node: ApiNode) => void
  deleteNode: (node: ApiNode) => void
}

type AttributeMap = Partial<Record<AttributeKey, string>>

const makeAttributeMap = (node: ApiNode) => {
  const attributes: AttributeMap = {}
  node.attributes.forEach(attribute => {
    attributes[attribute.key] = attribute.value
  })
  return attributes
}

const getAttributeArray = (attributes: AttributeMap) => {
  return Object.entries(attributes).map(([key, value]) => ({
    key: key as AttributeKey,
    value,
  }))
}

const isQuickKeyColor = (
  color: string | undefined
): color is QuickKeyColour => {
  return Object.values(QuickKeyColour).includes(color as QuickKeyColour)
}

const useProductSku = (productId: string | null) => {
  const productsStore = useProductsStore()
  const [sku, setSku] = useState<string>('')

  useEffect(() => {
    const fetchProductSku = async () => {
      if (!productId) {
        setSku('')
        return
      }

      const product = await productsStore.get(productId)
      if (product) {
        setSku(product.sku)
      }
    }
    fetchProductSku()
  }, [productId, productsStore])

  return sku
}

const QuickKeyPreview: FunctionComponent<{
  qk: QuickKeyKey
  attributes: AttributeMap
}> = ({ qk, attributes }) => {
  const sku = useProductSku(qk.node.product_id)

  const quickKeyUpdated = useMemo(() => {
    const showImage = attributes.showImage === '1'
    const color = isQuickKeyColor(attributes.color)
      ? attributes.color
      : QuickKeyColour.WHITE
    const viewData = {
      ...qk.viewData,
      label: attributes.label || '',
      showImage,
      color,
    }
    return { ...qk, viewData }
  }, [attributes, qk])

  return (
    <div className="vd-edit-qk-modal-row">
      <QKKey
        quickKey={quickKeyUpdated}
        setFocus={() => {}}
        onClick={() => {}}
      />
      <div className="vd-flex vd-flex--justify-center vd-flex--column">
        <Header level="3" type="sub-heading">
          {attributes.label}
        </Header>
        <Text type="supplementary">{sku}</Text>
      </div>
    </div>
  )
}

export const EditQuickKeyModal: FunctionComponent<EditQuickKeyModalProps> = ({
  quickKey,
  close,
  updateNode,
  deleteNode,
}) => {
  const t = useT()
  const { node } = quickKey

  const [attributes, setAttributes] = useState(makeAttributeMap(node))

  const updateNodeAttributes = useCallback(() => {
    const updatedNode = {
      ...node,
      attributes: getAttributeArray(attributes),
    }

    updateNode(updatedNode)
  }, [attributes, node, updateNode])

  const isFolder = quickKey.type === QuickKeyNodeType.FOLDER

  return (
    <Modal size="small" onClose={close}>
      <ModalHeader>
        {isFolder ? <T _str="Edit Folder" /> : <T _str="Edit Quick Key" />}
      </ModalHeader>
      <ModalContent>
        {isFolder ? null : (
          <QuickKeyPreview qk={quickKey} attributes={attributes} />
        )}
        <div className="vd-edit-qk-modal-row">
          <div className="vd-field">
            <FieldLabel
              htmlFor="quick-key-label"
              label={t('Quick Key Label')}
            />

            <FieldValue>
              <TextInput
                id="quick-key-label"
                value={attributes.label}
                onChange={e =>
                  setAttributes({ ...attributes, label: e.target.value })
                }
              />
            </FieldValue>
          </div>
        </div>
        <div className="vd-edit-qk-modal-row">
          <div className="vd-field">
            <FieldLabel label={t('Quick Key Label')} />
            <FieldValue>
              <QuickKeyColourPalette
                selectedColour={
                  isQuickKeyColor(attributes.color)
                    ? attributes.color
                    : QuickKeyColour.WHITE
                }
                onChange={color => setAttributes({ ...attributes, color })}
              />
            </FieldValue>
          </div>
        </div>
        {isFolder ? null : (
          <div className="vd-edit-qk-modal-row">
            <div className="vd-field">
              <label className="vd-value">
                <Switch
                  small
                  name="show-image"
                  checked={attributes.showImage === '1'}
                  onChange={e =>
                    setAttributes({
                      ...attributes,
                      showImage: e.target.checked ? '1' : '0',
                    })
                  }
                />
                <span className="vd-ml2">
                  <T
                    _str="Show image"
                    _context="Whether or not to show an image on a quick key"
                  />
                </span>
              </label>
            </div>
          </div>
        )}
      </ModalContent>
      <ModalActions>
        <div className="vd-btn-group vd-align-right">
          <Button onClick={() => deleteNode(node)} variant="no" modifier="text">
            <Icon icon="trash" className="vd-pr2" />
            <T _str="Delete Quick Key" />
          </Button>
          <Button onClick={updateNodeAttributes}>
            <T _str="Save" _context="Save quick key"></T>
          </Button>
        </div>
      </ModalActions>
    </Modal>
  )
}
